import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtMin, fmtTime } from "@/lib/format";
import { StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { ArrowLeft, CheckCircle2, Circle, Loader } from "lucide-react";

const STAGES = [
  { key: "order_received", label: "Order Received" },
  { key: "picking", label: "Picking" },
  { key: "packing", label: "Packing" },
  { key: "qc", label: "QC" },
  { key: "loading", label: "Loading" },
  { key: "dispatched", label: "Vehicle Dispatched" },
];

export default function BillDetail() {
  const { billNo } = useParams();
  const navigate = useNavigate();
  const [bill, setBill] = useState(null);

  useEffect(() => {
    api.get(`/bills/${billNo}`).then((r) => setBill(r.data)).catch(() => setBill(null));
  }, [billNo]);

  if (!bill) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  const st = bill.stages || {};

  // find the largest gap between consecutive completed stages
  let maxWait = null;
  const seq = ["order_received", "picking", "packing", "qc", "loading", "dispatched"];
  const endOf = (k) => {
    if (k === "order_received" || k === "dispatched") return st[k]?.at;
    return st[k]?.end;
  };
  const startOf = (k) => (k === "order_received" ? st[k]?.at : st[k]?.start);
  for (let i = 0; i < seq.length - 1; i++) {
    const e = endOf(seq[i]);
    const sNext = startOf(seq[i + 1]);
    if (e && sNext) {
      const gap = (new Date(sNext) - new Date(e)) / 60000;
      if (!maxWait || gap > maxWait.gap) maxWait = { gap, between: `${STAGES[i].label} → ${STAGES[i + 1].label}` };
    }
  }

  return (
    <div className="space-y-6 max-w-4xl" data-testid="bill-detail-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate("/bills")} data-testid="bill-back-button">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight metric" data-testid="bill-detail-number">{bill.bill_no}</h1>
            <StatusBadge status={bill.status} />
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            {bill.customer} · {bill.item_name} ({bill.sku}) · <span className="metric">{bill.quantity.toLocaleString()}</span> pcs
            {bill.cartons ? <> · <span className="metric">{bill.cartons}</span> cartons</> : null}
          </p>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        <div className="bg-card border border-border rounded-lg p-4">
          <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Total warehouse time</p>
          <p className="metric text-2xl font-extrabold mt-1" data-testid="bill-total-time">{fmtMin(bill.total_minutes)}</p>
        </div>
        <div className="bg-card border border-border rounded-lg p-4">
          <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Packing type</p>
          <p className="text-sm font-medium mt-1">{bill.packing_type || "—"}</p>
        </div>
        <div className={`border rounded-lg p-4 ${maxWait && maxWait.gap > 60 ? "border-signal-red/50 bg-signal-red/10" : "bg-card border-border"}`}>
          <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Largest wait</p>
          <p className="metric text-lg font-bold mt-1" data-testid="bill-longest-wait">{maxWait ? `${fmtMin(maxWait.gap)} (${maxWait.between})` : "—"}</p>
        </div>
      </div>

      <div className="bg-card border border-border rounded-lg p-5" data-testid="bill-timeline">
        <h2 className="text-xl font-bold uppercase tracking-tight mb-4">Lifecycle Timeline</h2>
        <ol className="relative border-l border-border ml-3 space-y-6">
          {STAGES.map((s) => {
            const entry = st[s.key];
            const isMilestone = s.key === "order_received" || s.key === "dispatched";
            const done = isMilestone ? !!entry?.at : !!entry?.end;
            const active = !done && entry?.start;
            let stageCls = "bg-muted text-muted-foreground";
            if (done) stageCls = "bg-signal-green/20 text-signal-green";
            else if (active) stageCls = "bg-signal-amber/20 text-signal-amber";
            let StageIcon = Circle;
            if (done) StageIcon = CheckCircle2;
            else if (active) StageIcon = Loader;
            return (
              <li key={s.key} className="ml-6" data-testid={`timeline-stage-${s.key}`}>
                <span className={`absolute -left-3 flex items-center justify-center w-6 h-6 rounded-full ${stageCls}`}>
                  <StageIcon className={`w-4 h-4${StageIcon === Loader ? " animate-spin" : ""}`} />
                </span>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                  <p className="font-semibold">{s.label}</p>
                  {entry?.duration != null && <span className="metric text-sm text-primary font-bold">{fmtMin(entry.duration)}</span>}
                  {active && <span className="text-[10px] font-mono uppercase tracking-widest text-signal-amber">in progress</span>}
                </div>
                <div className="text-xs text-muted-foreground metric mt-0.5 space-x-4">
                  {entry?.at && <span>at {fmtTime(entry.at)}</span>}
                  {entry?.start && <span>start {fmtTime(entry.start)}</span>}
                  {entry?.end && <span>end {fmtTime(entry.end)}</span>}
                </div>
                {(entry?.workers?.length || entry?.supervisor) && (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {entry.workers?.length ? `By: ${entry.workers.join(", ")}` : ""}
                    {entry.supervisor ? ` · Sup: ${entry.supervisor}` : ""}
                  </p>
                )}
                {entry?.delay_reason && (
                  <p className="text-xs text-signal-red mt-1 font-medium" data-testid={`timeline-delay-${s.key}`}>
                    Delay reason: {entry.delay_reason}{entry.remarks ? ` — ${entry.remarks}` : ""}
                  </p>
                )}
              </li>
            );
          })}
        </ol>
      </div>

      <div className="bg-card border border-border rounded-lg overflow-x-auto">
        <h2 className="text-xl font-bold uppercase tracking-tight px-5 pt-4">Tasks</h2>
        <table className="w-full text-sm mt-2" data-testid="bill-tasks-table">
          <thead>
            <tr className="border-b border-border text-left">
              {["Task", "Type", "Workers", "Start", "End", "Active", "Std", "Delay", "Reason", "Status"].map((h) => (
                <th key={h} className="px-4 py-2 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(bill.tasks || []).map((t) => (
              <tr key={t.task_id} className="border-b border-border/50">
                <td className="px-4 py-2.5 metric text-primary">{t.task_id}</td>
                <td className="px-4 py-2.5 capitalize">{t.type}</td>
                <td className="px-4 py-2.5">{(t.assigned_to_names || []).join(", ")}</td>
                <td className="px-4 py-2.5 metric text-xs">{fmtTime(t.start_time)}</td>
                <td className="px-4 py-2.5 metric text-xs">{fmtTime(t.end_time)}</td>
                <td className="px-4 py-2.5 metric">{fmtMin(t.active_minutes)}</td>
                <td className="px-4 py-2.5 metric text-muted-foreground">{t.standard_minutes ? `${t.standard_minutes}m` : "—"}</td>
                <td className={`px-4 py-2.5 metric ${t.delay_minutes > 0 ? "text-signal-red font-bold" : "text-muted-foreground"}`}>
                  {t.delay_minutes > 0 ? `+${Math.round(t.delay_minutes)}m` : "—"}
                </td>
                <td className="px-4 py-2.5 text-xs text-muted-foreground">{t.delay_reason || "—"}</td>
                <td className="px-4 py-2.5"><StatusBadge status={t.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
